import { useMemo, useState } from 'react';

import { AxiosRequestConfig } from 'axios';
import { ToolsService } from 'services/tools-service';
import { CustomApiRequestToolConfig } from 'shared/interfaces/tools/CustomApiRequest';

interface UseApiTesterProps {
  toolConfig?: CustomApiRequestToolConfig;
}

export interface TestParamsValidation {
  isValid: boolean;
  error: string;
  parsed: Record<string, unknown>;
}

export function useApiTester({ toolConfig }: UseApiTesterProps) {
  const [testParams, setTestParams] = useState<string>('{}');
  const [isExecuting, setIsExecuting] = useState(false);
  const [apiResponse, setApiResponse] = useState<string>('');
  const [hasRendered, setHasRendered] = useState(false);

  // Utility function to get nested value from object by path
  const getNestedValue = (obj: Record<string, unknown>, path: string[]): unknown => {
    let current: unknown = obj;

    for (const key of path) {
      if (typeof current === 'object' && current !== null) {
        current = (current as Record<string, unknown>)[key];
      } else {
        return undefined;
      }
    }

    return current;
  };

  // Utility function to replace template variables
  const replaceTemplateVariables = (template: string, params: Record<string, unknown>): string => {
    if (!template || !params) return template;

    return template.replace(/\{\{([^}]+)\}\}/g, (_, variablePath: string) => {
      const value = getNestedValue(params, variablePath.split('.'));

      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }

      return String(value ?? '');
    });
  };

  // Utility function to process template object (headers, query params)
  const processTemplateObject = (
    templateObj: Record<string, string> = {},
    params: Record<string, unknown>
  ): Record<string, string> => {
    const result: Record<string, string> = {};
    Object.entries(templateObj).forEach(([key, template]) => {
      result[key] = replaceTemplateVariables(template, params);
    });
    return result;
  };

  const testParamsValidation = useMemo((): TestParamsValidation => {
    // Reset rendered state when test params change
    setHasRendered(false);

    if (!testParams || testParams.trim() === '') {
      return { isValid: false, error: '', parsed: {} };
    }

    try {
      const parsed = JSON.parse(testParams);
      return { isValid: true, error: '', parsed };
    } catch (error) {
      return { isValid: false, error: 'Invalid JSON format', parsed: {} };
    }
  }, [testParams]);

  const renderedRequest = useMemo((): AxiosRequestConfig | null => {
    if (!testParamsValidation.isValid || !toolConfig?.requestConfiguration) {
      return null;
    }

    const { parsed } = testParamsValidation;
    const { requestConfiguration } = toolConfig;

    // Process all template parts using utility functions
    const url = replaceTemplateVariables(requestConfiguration.urlTemplate || '', parsed);
    const headers = processTemplateObject(requestConfiguration.headersTemplate, parsed);
    const params = processTemplateObject(requestConfiguration.queryParametersTemplate, parsed);

    // Process body template
    const bodyTemplate = requestConfiguration.bodyTemplate || '';
    const data = bodyTemplate.trim() ? replaceTemplateVariables(bodyTemplate, parsed) : undefined;

    return {
      method: requestConfiguration.method || 'GET',
      url,
      headers,
      params,
      data
    };
  }, [testParamsValidation, toolConfig]);

  const executeRequest = async () => {
    if (!testParamsValidation.isValid || !testParamsValidation.parsed || !toolConfig) return;

    setIsExecuting(true);
    setApiResponse('');

    try {
      const result = await ToolsService.testCustomApiRequest({
        toolConfig,
        testParams: testParamsValidation.parsed
      });

      // Server formats everything - we just display it
      setApiResponse(result.formattedResponse);
    } catch (error) {
      setApiResponse(`Service Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExecuting(false);
    }
  };

  const renderRequest = () => {
    setHasRendered(true);
  };

  const resetResponse = () => {
    setApiResponse('');
  };

  return {
    // State
    testParams,
    isExecuting,
    apiResponse,
    hasRendered,

    // Computed values
    testParamsValidation,
    renderedRequest,

    // Actions
    setTestParams,
    renderRequest,
    executeRequest,
    resetResponse
  };
}
